import { z } from 'zod';
import { JobInterestState } from '../joblisting/listing.enum';
import { BookingOrigin, BookingState, JobListingState } from './booking.enums';
import { JobListingCompensationTier, JobLocation } from './hiring.enums';
import { MoneySchema, PaginationParamsSchema } from '../common';

const jsonRecordSchema = z.record(z.string(), z.unknown());

/** `POST /marketplace/bookings` */
export const CreateMarketplaceBookingRequestSchema = z
  .object({
    vendorId: z.uuid(),
    origin: z.enum(BookingOrigin),
    eventId: z.uuid().optional(),
    jobListingId: z.uuid().optional(),
    sourceIoiId: z.uuid().optional(),
    agreedAmount: z.coerce.number().nonnegative().optional(),
    scopeOfWork: z.string().max(5000).optional(),
  })
  .superRefine((data, ctx) => {
    if (data.origin === BookingOrigin.EVENT_DIRECT && !data.eventId) {
      ctx.addIssue({
        code: 'custom',
        message: 'eventId is required when origin is EVENT_DIRECT',
        path: ['eventId'],
      });
    }
    if (data.origin === BookingOrigin.MARKETPLACE_DIRECT) {
      if (data.eventId) {
        ctx.addIssue({
          code: 'custom',
          message: 'eventId must not be set when origin is MARKETPLACE_DIRECT',
          path: ['eventId'],
        });
      }
      if (data.sourceIoiId) {
        ctx.addIssue({
          code: 'custom',
          message: 'sourceIoiId must not be set when origin is MARKETPLACE_DIRECT',
          path: ['sourceIoiId'],
        });
      }
    }
    if (data.origin === BookingOrigin.JOB_LISTING) {
      if (!data.sourceIoiId) {
        ctx.addIssue({
          code: 'custom',
          message: 'sourceIoiId is required when origin is JOB_LISTING',
          path: ['sourceIoiId'],
        });
      }
      if (data.eventId) {
        ctx.addIssue({
          code: 'custom',
          message: 'eventId must not be set when origin is JOB_LISTING',
          path: ['eventId'],
        });
      }
    }
    if (data.sourceIoiId && data.origin !== BookingOrigin.JOB_LISTING) {
      ctx.addIssue({
        code: 'custom',
        message: 'sourceIoiId is only allowed when origin is JOB_LISTING',
        path: ['sourceIoiId'],
      });
    }
  });

/**
 * Assigned-event execution context on a booking.
 * Schedule + go-live window for the linked event. Full readiness /
 * streaming live on `GET /marketplace/vendor/events/:id`.
 *
 * `requiredStartAt` / `requiredEndAt` are derived from the event
 * schedule until `Booking.requiredStartAt` / `requiredEndAt` are written.
 */
export const MarketplaceBookingAssignedEventSchema = z.object({
  id: z.uuid(),
  title: z.string(),
  state: z.string(),
  timezone: z.string(),
  scheduledStart: z.iso.datetime(),
  scheduledStartLocal: z.iso.datetime({ local: true, offset: false }),
  scheduledEnd: z.iso.datetime(),
  scheduledEndLocal: z.iso.datetime({ local: true, offset: false }),
  requiredStartAt: z.iso.datetime(),
  requiredEndAt: z.iso.datetime(),
  /** Earliest go-live instant (scheduledStart − early window). */
  goLiveEligibleAt: z.iso.datetime().nullable(),
});

export const MarketplaceBookingResponseSchema = z.object({
  id: z.uuid(),
  eventId: z.uuid().nullable(),
  eventLinked: z.boolean(),
  hostProfileId: z.uuid(),
  vendorProfileId: z.uuid(),
  origin: z.enum(BookingOrigin),
  jobListingId: z.uuid().nullable(),
  sourceIoiId: z.uuid().nullable(),
  state: z.enum(BookingState),
  /** Server-inherited currency + minor-unit amount (MONEY_MULTI_CURRENCY.md §4). */
  agreedAmount: MoneySchema.nullable(),
  scopeOfWork: z.string().nullable(),
  /**
   * Direct Hire Assign Role fields from `Booking.serviceDetails`.
   * Null when the booking has no structured role payload (non–Direct Hire).
   */
  role: z.string().nullable(),
  /** Invite modal "Description" — same source as responsibilities when set. */
  description: z.string().nullable(),
  responsibilities: z.string().nullable(),
  requirements: z.string().nullable(),
  location: z.enum(JobLocation).nullable(),
  notes: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  /**
   * Linked-event schedule + go-live context. Populated on vendor
   * booking list/detail when `eventId` is set; otherwise null.
   */
  assignedEvent: MarketplaceBookingAssignedEventSchema.nullable(),
});

/** `POST /marketplace/bookings` — create acknowledgement */
export const CreateMarketplaceBookingResponseSchema = z.object({
  bookingId: z.uuid(),
  booking: MarketplaceBookingResponseSchema,
});

const bookingTransitionReasonSchema = z.string().trim().min(5).max(500);

/** `POST /marketplace/bookings/:id/transition` (host, vendor, admin) */
export const TransitionMarketplaceBookingRequestSchema = z
  .object({
    targetState: z.enum(BookingState),
    reason: bookingTransitionReasonSchema.optional(),
  })
  .superRefine((data, ctx) => {
    const requiresReason =
      data.targetState === BookingState.REJECTED ||
      data.targetState === BookingState.CANCELLED ||
      data.targetState === BookingState.IN_DISPUTE;
    if (requiresReason && data.reason === undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'reason is required for this transition',
        path: ['reason'],
      });
    }
  });

export const TransitionMarketplaceBookingResponseSchema = z.object({
  booking: MarketplaceBookingResponseSchema,
});

/** `POST /marketplace/bookings/:id/complete` — dual-ack manual completion */
export const CompleteMarketplaceBookingRequestSchema = z.object({
  notes: z.string().max(2000).optional(),
});

/** `POST /marketplace/bookings/:id/admin-force-complete` — TRUST admin override */
export const ForceCompleteMarketplaceBookingRequestSchema = z.object({
  notes: z.string().max(2000).optional(),
});

export const CompleteMarketplaceBookingResponseSchema = z.object({
  booking: MarketplaceBookingResponseSchema,
  hostAcked: z.boolean(),
  vendorAcked: z.boolean(),
  completed: z.boolean(),
  forced: z.boolean().optional(),
});

const jobListingEventTitleRefine = (
  data: {
    eventId?: string | undefined;
    eventTitle?: string | undefined;
    title?: string | undefined;
  },
  ctx: z.RefinementCtx,
) => {
  if (data.eventId) {
    return;
  }
  const standaloneTitle = data.eventTitle?.trim() || data.title?.trim();
  if (!standaloneTitle) {
    ctx.addIssue({
      code: 'custom',
      message: 'eventTitle is required when eventId is not provided',
      path: ['eventTitle'],
    });
  }
};

const jobListingRequiredDateRefine = (
  data: {
    requiredDateLocal?: string | undefined;
    timezone?: string | undefined;
  },
  ctx: z.RefinementCtx,
) => {
  if (data.requiredDateLocal !== undefined && !data.timezone?.trim()) {
    ctx.addIssue({
      code: 'custom',
      message: 'timezone is required when requiredDateLocal is provided',
      path: ['timezone'],
    });
  }
};

const MarketplaceJobListingBodyFieldsSchema = z.object({
  /** Standalone event title — required when `eventId` is absent. Ignored when linked. */
  eventTitle: z.string().min(1).max(200).optional(),
  /** @deprecated Use `eventTitle`. Kept for backward compatibility. */
  title: z.string().min(1).max(200).optional(),
  description: z.string().min(1).max(5000),
  responsibilities: z.string().min(1),
  requirements: z.string().min(1),
  role: z.string().min(1).max(120),
  requiredCount: z.coerce.number().int().min(1),
  /** Wall-clock local datetime in `timezone` — same contract as Event `scheduledStartLocal`. */
  requiredDateLocal: z.iso.datetime({ local: true, offset: false }).optional(),
  /** IANA timezone for `requiredDateLocal` (e.g. Africa/Lagos). */
  timezone: z.string().min(1).optional(),
  /**
   * @deprecated Prefer `requiredDateLocal` + `timezone`.
   * When sent alone, treated as wall-clock digits (Event wizard legacy path).
   */
  requiredDate: z.iso.datetime().optional(),
  estimatedDurationMinutes: z.coerce.number().int().positive().optional(),
  location: z.enum(JobLocation),
  notes: z.string().max(2000).optional(),
  compensation: z.coerce.number().nonnegative().optional(),
  attachments: jsonRecordSchema.optional(),
  eventId: z.uuid().optional(),
});

const MarketplaceJobListingBodySchema = MarketplaceJobListingBodyFieldsSchema.superRefine(
  jobListingEventTitleRefine,
).superRefine(jobListingRequiredDateRefine);

/** Publish/create body without event-title guard (linked listings resolve title at read time). */
const PublishableJobListingBodyFieldsSchema = z.object({
  eventTitle: z.string().min(1).max(200).optional(),
  title: z.string().min(1).max(200).optional(),
  description: z.string().min(1).max(5000),
  responsibilities: z.string().min(1),
  requirements: z.string().min(1),
  role: z.string().min(1).max(120),
  requiredCount: z.coerce.number().int().min(1),
  location: z.enum(JobLocation),
  eventId: z.uuid().optional(),
});

const PublishableJobListingBodySchema = PublishableJobListingBodyFieldsSchema;

/** `POST /marketplace/job-listings` — publish immediately unless `saveAsDraft` */
export const CreateMarketplaceJobListingRequestSchema =
  MarketplaceJobListingBodyFieldsSchema.partial()
    .extend({
      saveAsDraft: z.boolean().optional().default(false),
      /**
       * Optional vendor to invite as soon as the listing is created (or
       * when a DRAFT is published). Alias of a single-item `vendorIds`.
       */
      vendorId: z.uuid().optional(),
      /** Optional vendors to attach; each receives a job invite. */
      vendorIds: z.array(z.uuid()).min(1).max(10).optional(),
    })
    .superRefine(jobListingRequiredDateRefine)
    .superRefine((data, ctx) => {
      const attached = new Set(data.vendorIds ?? []);
      if (data.vendorId) {
        attached.add(data.vendorId);
      }
      if (data.requiredCount !== undefined && attached.size > data.requiredCount) {
        ctx.addIssue({
          code: 'custom',
          message: 'Cannot attach more vendors than requiredCount',
          path: ['vendorIds'],
        });
      }
      if (data.saveAsDraft) {
        return;
      }
      const result = MarketplaceJobListingBodySchema.safeParse(data);
      if (!result.success) {
        ctx.addIssue({
          code: 'custom',
          message: result.error.issues.map((issue) => issue.message).join('; '),
        });
      }
    });

export const UpdateMarketplaceJobListingRequestSchema =
  MarketplaceJobListingBodyFieldsSchema.partial().superRefine(jobListingRequiredDateRefine);

export const ListMarketplaceJobListingsQuerySchema = z.object({
  ...PaginationParamsSchema.shape,
  location: z.enum(JobLocation).optional(),
  /** Case-insensitive match on listing title, role, linked event title, or host business name */
  search: z.string().trim().min(2).max(100).optional(),
  /** Comma-separated role slugs, e.g. `camera-operator,sound-engineer` */
  roles: z.string().trim().min(1).max(500).optional(),
  requiredDateFrom: z.string().optional(),
  requiredDateTo: z.string().optional(),
  minCompensation: z.coerce.number().int().min(0).optional(),
  maxCompensation: z.coerce.number().int().min(0).optional(),
  compensationTier: z.enum(JobListingCompensationTier).optional(),
  /** When true, only listings whose requiredDate falls on a day the authenticated vendor marked available */
  matchMyAvailability: z
    .enum(['true', 'false'])
    .optional()
    .transform((value) => value === 'true'),
});

/** `GET /marketplace/job-listings/mine` (host) */
export const ListHostMarketplaceJobListingsQuerySchema = z.object({
  ...PaginationParamsSchema.shape,
  state: z.enum(JobListingState).optional(),
  /** Case-insensitive match on listing title, role, or linked event title */
  search: z.string().trim().min(2).max(100).optional(),
});

/** Required persisted fields before a DRAFT job listing can be published */
export const PublishReadyJobListingSchema = PublishableJobListingBodySchema.superRefine(
  (data, ctx) => {
    if (data.eventId) {
      return;
    }
    const standaloneTitle = data.eventTitle?.trim() || data.title?.trim();
    if (!standaloneTitle) {
      ctx.addIssue({
        code: 'custom',
        message: 'eventTitle is required for standalone job listings',
        path: ['eventTitle'],
      });
    }
  },
);

/** `POST /marketplace/job-listings/:id/publish` (host owner) */
export const PublishMarketplaceJobListingResponseSchema = z.object({
  id: z.uuid(),
  state: z.literal('OPEN'),
});

export const MarketplaceJobListingResponseSchema = z.object({
  id: z.uuid(),
  hostProfileId: z.uuid(),
  eventId: z.uuid().nullable(),
  /** Resolved display title — linked events use the current Event title. */
  title: z.string(),
  eventTitle: z.string().nullable(),
  notes: z.string().nullable(),
  description: z.string(),
  responsibilities: z.string(),
  requirements: z.string(),
  role: z.string(),
  requiredCount: z.number().int().min(1),
  /** UTC instant (Event `scheduledStart` analogue). */
  requiredDate: z.string().nullable(),
  /** Wall-clock string, no offset (Event `scheduledStartLocal` analogue). */
  requiredDateLocal: z.string().nullable(),
  timezone: z.string().nullable(),
  estimatedDurationMinutes: z.number().int().nullable(),
  location: z.enum(JobLocation),
  /** Host-set compensation for the listing (MoneyDto, MONEY_MULTI_CURRENCY.md §4). */
  compensation: MoneySchema.nullable(),
  attachments: z.unknown().nullable(),
  state: z.enum(JobListingState),
  proposalCount: z.number().int(),
  cancelledAt: z.string().nullable(),
  cancelledReason: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

/** `POST /marketplace/job-listings/:id/cancel` (host owner) — mirrors HostEventCancelResponseSchema shape */
export const CancelMarketplaceJobListingRequestSchema = z.object({
  reason: z.string().trim().min(5).max(500).optional(),
});

export const CancelMarketplaceJobListingResponseSchema = z.object({
  id: z.uuid(),
  state: z.literal('CANCELLED'),
  cancelledAt: z.iso.datetime().nullable(),
});

/**
 * `POST /marketplace/job-listings/:id/close` (host owner) —
 * pause new applications (`OPEN` → `SUSPENDED`). Pending IoIs are kept.
 */
export const CloseMarketplaceJobListingResponseSchema = z.object({
  id: z.uuid(),
  state: z.literal(JobListingState.SUSPENDED),
});

/**
 * `POST /marketplace/job-listings/:id/open` (host owner) —
 * resume applications (`SUSPENDED` | `CLOSED` → `OPEN`) when capacity remains.
 */
export const OpenMarketplaceJobListingResponseSchema = z.object({
  id: z.uuid(),
  state: z.literal(JobListingState.OPEN),
});

/** `POST .../indications-of-interest` */
export const SubmitMarketplaceIndicationOfInterestRequestSchema = z.object({
  coverMessage: z.string().min(1).max(2000).optional(),
  proposedAmount: z.coerce.number().nonnegative().optional(),
  availabilityNotes: z.string().max(2000).optional(),
  attachments: jsonRecordSchema.optional(),
});

export const MarketplaceIndicationOfInterestResponseSchema = z.object({
  id: z.uuid(),
  jobListingId: z.uuid(),
  vendorProfileId: z.uuid(),
  coverMessage: z.string().nullable(),
  /** Vendor-proposed compensation (MoneyDto — currency inherits from parent listing). */
  proposedAmount: MoneySchema.nullable(),
  availabilityNotes: z.string().nullable(),
  state: z.enum(JobInterestState),
  resultingBookingId: z.uuid().nullable(),
  withdrawnAt: z.string().nullable(),
  selectedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

/** Host review row — IoI plus vendor display label */
export const MarketplaceIndicationOfInterestHostItemSchema =
  MarketplaceIndicationOfInterestResponseSchema.extend({
    vendorCompanyName: z.string().nullable(),
  });

export const HostIndicationOfInterestSortBy = {
  CREATED_AT: 'createdAt',
  UPDATED_AT: 'updatedAt',
  PROPOSED_AMOUNT: 'proposedAmount',
} as const;

export const SortOrder = {
  ASC: 'asc',
  DESC: 'desc',
} as const;

/** `GET /marketplace/job-listings/indications-of-interest/mine` (host inbox) */
export const ListHostMarketplaceIndicationsQuerySchema = z.object({
  ...PaginationParamsSchema.shape,
  jobListingId: z.uuid().optional(),
  state: z.enum(JobInterestState).optional(),
  /** Case-insensitive match on job listing title or linked event title */
  search: z.string().trim().min(2).max(100).optional(),
  sortBy: z.enum(HostIndicationOfInterestSortBy).default(HostIndicationOfInterestSortBy.CREATED_AT),
  sortOrder: z.enum(SortOrder).default(SortOrder.DESC),
});

/** Host inbox row — IoI with applicant + parent job listing summary */
export const MarketplaceHostIndicationOfInterestInboxItemSchema =
  MarketplaceIndicationOfInterestHostItemSchema.extend({
    vendorDisplayName: z.string(),
    vendorAvatarUrl: z.url().nullable(),
    vendorServiceCategoryLabels: z.array(z.string()),
    vendorEquipmentLabels: z.array(z.string()),
    vendorIsVerified: z.boolean(),
    jobListingTitle: z.string(),
    jobListingRole: z.string(),
    jobListingState: z.enum(JobListingState),
    jobListingLocation: z.enum(JobLocation),
    jobListingEventId: z.uuid().nullable(),
    jobListingEventTitle: z.string().nullable(),
  });

/** `GET /marketplace/indications-of-interest/mine` (vendor applications inbox) */
export const ListVendorMarketplaceIndicationsQuerySchema = z.object({
  ...PaginationParamsSchema.shape,
  jobListingId: z.uuid().optional(),
  state: z.enum(JobInterestState).optional(),
  /** Case-insensitive match on job listing title or linked event title */
  search: z.string().trim().min(2).max(100).optional(),
  sortBy: z.enum(HostIndicationOfInterestSortBy).default(HostIndicationOfInterestSortBy.CREATED_AT),
  sortOrder: z.enum(SortOrder).default(SortOrder.DESC),
});

/** Vendor applications inbox row — IoI with parent job listing summary */
export const MarketplaceVendorIndicationOfInterestInboxItemSchema =
  MarketplaceIndicationOfInterestResponseSchema.extend({
    hostBusinessName: z.string().nullable(),
    jobListingTitle: z.string(),
    jobListingRole: z.string(),
    jobListingState: z.enum(JobListingState),
    jobListingLocation: z.enum(JobLocation),
    jobListingEventId: z.uuid().nullable(),
    jobListingEventTitle: z.string().nullable(),
    jobListingRequiredDate: z.string().nullable(),
    jobListingCompensation: MoneySchema.nullable(),
  });
